import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslations } from '../hooks/useTranslations';
import Logo from './Logo';
import LanguageSelector from './LanguageSelector';
import './CustomerHeader.css';

export interface CustomerHeaderProps {
  /** Show back button */
  showBackButton?: boolean;
  /** Back button click handler (default: navigate to home) */
  onBackClick?: () => void;
  /** Show tagline below logo */
  showTagline?: boolean;
  /** Show voice search button */
  showVoiceSearch?: boolean;
  /** Show loyalty button */
  showLoyaltyButton?: boolean;
  /** Cart count (auto-calculated if not provided) */
  cartCount?: number;
  /** Notification count */
  notificationCount?: number;
  /** Custom search handler */
  onSearch?: (query: string) => void;
  /** Custom cart click handler */
  onCartClick?: () => void;
  /** Variant: 'default' | 'sticky' | 'compact' */
  variant?: 'default' | 'sticky' | 'compact';
  /** Show profile dropdown or simple button */
  showProfileDropdown?: boolean;
  /** Custom className */
  className?: string;
}

const CustomerHeader: React.FC<CustomerHeaderProps> = ({
  showBackButton = false,
  onBackClick,
  showTagline: _showTagline = false,
  showVoiceSearch: _showVoiceSearch = false,
  showLoyaltyButton = false,
  cartCount: propCartCount,
  notificationCount = 0,
  onSearch,
  onCartClick,
  variant = 'default',
  showProfileDropdown: _showProfileDropdown = false,
  className = ''
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout: _logout } = useAuth();
  const t = useTranslations();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [cartCount, setCartCount] = useState(propCartCount ?? 0);
  const [_wishlistCount, setWishlistCount] = useState(0);
  const [notificationCountState, setNotificationCountState] = useState(notificationCount || 0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });
  const profileRef = useRef<HTMLDivElement>(null);

  // Auto-calculate wishlist count
  useEffect(() => {
    const updateWishlistCount = () => {
      try {
        const saved = localStorage.getItem('wishlist');
        if (saved) {
          const wishlist = JSON.parse(saved);
          const count = Array.isArray(wishlist) ? wishlist.length : 0;
          setWishlistCount(count);
          console.log('❤️ CustomerHeader: Wishlist count updated:', count);
        } else {
          setWishlistCount(0);
          console.log('❤️ CustomerHeader: No wishlist found');
        }
      } catch (error) {
        console.error('Error loading wishlist count:', error);
        setWishlistCount(0);
      }
    };

    updateWishlistCount();
    
    // Listen for wishlist updates
    const handleWishlistUpdate = () => {
      updateWishlistCount();
    };
    
    window.addEventListener('storage', handleWishlistUpdate);
    // Also listen for custom events (when wishlist is updated in same tab)
    window.addEventListener('wishlistUpdated', handleWishlistUpdate);
    
    // Poll for changes (fallback)
    const interval = setInterval(updateWishlistCount, 1000);
    
    return () => {
      window.removeEventListener('storage', handleWishlistUpdate);
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
      clearInterval(interval);
    };
  }, []);

  // Auto-calculate notification count
  useEffect(() => {
    const updateNotificationCount = () => {
      try {
        const saved = localStorage.getItem('notifications');
        if (saved) {
          const notifications = JSON.parse(saved);
          const unreadCount = Array.isArray(notifications) 
            ? notifications.filter((n: any) => !n.read && !n.is_read).length 
            : 0;
          setNotificationCountState(unreadCount);
          console.log('🔔 CustomerHeader: Notification count updated:', unreadCount);
        } else {
          setNotificationCountState(0);
        }
      } catch (error) {
        console.error('Error loading notification count:', error);
        setNotificationCountState(0);
      }
    };

    updateNotificationCount();
    
    // Listen for notification updates
    const handleNotificationUpdate = () => {
      updateNotificationCount();
    };
    
    window.addEventListener('storage', handleNotificationUpdate);
    window.addEventListener('notificationCreated', handleNotificationUpdate);
    
    // Poll for changes (fallback)
    const interval = setInterval(updateNotificationCount, 1000);
    
    return () => {
      window.removeEventListener('storage', handleNotificationUpdate);
      window.removeEventListener('notificationCreated', handleNotificationUpdate);
      clearInterval(interval);
    };
  }, []);

  // Auto-calculate cart count if not provided
  useEffect(() => {
    if (propCartCount === undefined) {
      const updateCartCount = () => {
        try {
          const cartData = localStorage.getItem('cart');
          const multiShopCart = localStorage.getItem('multiShopCart');
          let count = 0;
          let actualItemCount = 0;
          let diagnosticInfo: any = {
            oldCartCount: 0,
            nestedCartCount: 0,
            flatCartCount: 0,
            totalCalculated: 0,
            actualItems: []
          };
          
          console.log('🛒 CustomerHeader: updateCartCount called');
          console.log('🛒 CustomerHeader: cartData exists:', !!cartData);
          console.log('🛒 CustomerHeader: multiShopCart exists:', !!multiShopCart);
          
          // Count from old cart format
          if (cartData) {
            try {
              const cart = JSON.parse(cartData);
              if (Array.isArray(cart)) {
                const oldCount = cart.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0);
                count += oldCount;
                diagnosticInfo.oldCartCount = oldCount;
                console.log('🛒 CustomerHeader: Old cart format count:', oldCount);
              }
            } catch (e) {
              console.warn('⚠️ CustomerHeader: Error parsing old cart format:', e);
            }
          }
          
          // Count from multiShopCart format
          if (multiShopCart) {
            try {
              const multiCart = JSON.parse(multiShopCart);
              console.log('🛒 CustomerHeader: Parsed multiShopCart:', {
                isArray: Array.isArray(multiCart),
                length: Array.isArray(multiCart) ? multiCart.length : 'N/A',
                firstItem: Array.isArray(multiCart) && multiCart.length > 0 ? multiCart[0] : null
              });
              
              if (Array.isArray(multiCart) && multiCart.length > 0) {
                const firstItem = multiCart[0];
                console.log('🛒 CustomerHeader: First item structure:', {
                  hasShop: !!firstItem?.shop,
                  hasItems: !!firstItem?.items,
                  isItemsArray: Array.isArray(firstItem?.items),
                  hasProduct: !!firstItem?.product,
                  hasQuantity: firstItem?.quantity !== undefined,
                  fullFirstItem: firstItem
                });
                
                let formatDetected = false;
                
                // Check if it's the nested format: { shop, items: [{ product, quantity }] }
                if (firstItem?.shop && firstItem?.items && Array.isArray(firstItem.items)) {
                  // Nested structure: array of shop carts
                  console.log('🛒 CustomerHeader: Detected NESTED format');
                  formatDetected = true;
                  let nestedCount = 0;
                  multiCart.forEach((shopCart: any, index: number) => {
                    if (shopCart.items && Array.isArray(shopCart.items)) {
                      const shopCount = shopCart.items.reduce((sum: number, item: any) => {
                        const qty = item.quantity || 1;
                        diagnosticInfo.actualItems.push({
                          shop: shopCart.shop?.name || 'unknown',
                          product: item.product?.name || 'unknown',
                          quantity: qty
                        });
                        return sum + qty;
                      }, 0);
                      nestedCount += shopCount;
                      console.log(`🛒 CustomerHeader: Shop ${index} (${shopCart.shop?.name || 'unknown'}): ${shopCount} items (${shopCart.items.length} unique products)`);
                    }
                  });
                  count += nestedCount;
                  diagnosticInfo.nestedCartCount = nestedCount;
                  actualItemCount = diagnosticInfo.actualItems.length;
                } 
                
                // Check if it's the flat format: { product, shop, quantity }
                if (!formatDetected && (firstItem?.product || firstItem?.quantity !== undefined)) {
                  // Flat structure: array of { product, shop, quantity }
                  console.log('🛒 CustomerHeader: Detected FLAT format');
                  formatDetected = true;
                  const flatCount = multiCart.reduce((sum: number, item: any) => {
                    const qty = item.quantity || 1;
                    diagnosticInfo.actualItems.push({
                      shop: item.shop?.name || 'unknown',
                      product: item.product?.name || 'unknown',
                      quantity: qty
                    });
                    return sum + qty;
                  }, 0);
                  count += flatCount;
                  diagnosticInfo.flatCartCount = flatCount;
                  actualItemCount = multiCart.length;
                }
                
                // Fallback: Try to count items regardless of format
                if (!formatDetected) {
                  console.warn('⚠️ CustomerHeader: Unknown cart format, trying fallback counting...');
                  console.warn('⚠️ CustomerHeader: First item:', JSON.stringify(firstItem, null, 2));
                  
                  // Try nested format as fallback (check all items, not just first)
                  let hasNestedFormat = multiCart.some((item: any) => item.shop && item.items && Array.isArray(item.items));
                  if (hasNestedFormat) {
                    console.log('🛒 CustomerHeader: Fallback - Detected NESTED format by checking all items');
                    let nestedCount = 0;
                    multiCart.forEach((shopCart: any, index: number) => {
                      if (shopCart.items && Array.isArray(shopCart.items)) {
                        const shopCount = shopCart.items.reduce((sum: number, item: any) => {
                          const qty = item.quantity || 1;
                          diagnosticInfo.actualItems.push({
                            shop: shopCart.shop?.name || 'unknown',
                            product: item.product?.name || 'unknown',
                            quantity: qty
                          });
                          return sum + qty;
                        }, 0);
                        nestedCount += shopCount;
                        console.log(`🛒 CustomerHeader: Fallback - Shop ${index} (${shopCart.shop?.name || 'unknown'}): ${shopCount} items`);
                      }
                    });
                    count += nestedCount;
                    diagnosticInfo.nestedCartCount = nestedCount;
                    actualItemCount = diagnosticInfo.actualItems.length;
                  } else {
                    // Try flat format as fallback
                    let hasFlatFormat = multiCart.some((item: any) => item.product || item.quantity !== undefined);
                    if (hasFlatFormat) {
                      console.log('🛒 CustomerHeader: Fallback - Detected FLAT format by checking all items');
                      const flatCount = multiCart.reduce((sum: number, item: any) => {
                        const qty = item.quantity || 1;
                        diagnosticInfo.actualItems.push({
                          shop: item.shop?.name || 'unknown',
                          product: item.product?.name || 'unknown',
                          quantity: qty
                        });
                        return sum + qty;
                      }, 0);
                      count += flatCount;
                      diagnosticInfo.flatCartCount = flatCount;
                      actualItemCount = multiCart.length;
                    } else {
                      console.error('❌ CustomerHeader: Cannot determine cart format even with fallback!');
                      console.error('❌ CustomerHeader: Full cart structure:', JSON.stringify(multiCart, null, 2));
                    }
                  }
                }
              } else {
                console.log('🛒 CustomerHeader: multiShopCart is empty or not an array');
              }
            } catch (e) {
              console.error('❌ CustomerHeader: Error parsing multiShopCart:', e);
              console.error('❌ CustomerHeader: Raw multiShopCart:', multiShopCart);
            }
          }
          
          diagnosticInfo.totalCalculated = count;
          diagnosticInfo.actualItemCount = actualItemCount;
          
          console.log('🛒 CustomerHeader: Final calculated count:', count);
          console.log('🛒 CustomerHeader: Diagnostic Info:', diagnosticInfo);
          console.log('🛒 CustomerHeader: Actual items in cart:', diagnosticInfo.actualItems);
          
          // Check for mismatch
          if (count > 0 && actualItemCount === 0) {
            console.error('❌ MISMATCH: Count shows', count, 'but no items found in cart!');
          } else if (count === 0 && actualItemCount > 0) {
            console.error('❌ MISMATCH: Items found (', actualItemCount, ') but count is 0!');
          } else if (count !== actualItemCount && actualItemCount > 0) {
            console.warn('⚠️ MISMATCH: Count (', count, ') does not match item count (', actualItemCount, ')');
          }
          
          // Always update to ensure UI reflects current state (force update)
          setCartCount(count);
          console.log('✅ CustomerHeader: Cart count set to:', count);
        } catch (error) {
          console.error('❌ CustomerHeader: Error reading cart:', error);
          setCartCount(0);
        }
      };

      // Initial update
      updateCartCount();
      
      // Poll every 200ms for more responsive updates (fallback if events are missed)
      const interval = setInterval(() => {
        updateCartCount();
      }, 200);
      
      // Listen for cart update events
      const handleCartUpdate = (event: Event) => {
        console.log('🛒 CustomerHeader: cartUpdated event received', event);
        // Update immediately first
        updateCartCount();
        // Then update again with small delays as fallbacks to ensure we catch the update
        setTimeout(() => {
          updateCartCount();
        }, 10);
        setTimeout(() => {
          updateCartCount();
        }, 100);
        setTimeout(() => {
          updateCartCount();
        }, 300);
      };
      
      window.addEventListener('cartUpdated', handleCartUpdate);
      
      // Also listen for storage events (in case localStorage is updated from another tab/window)
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === 'multiShopCart' || e.key === 'cart') {
          console.log('🛒 CustomerHeader: Storage event detected, updating count');
          updateCartCount();
        }
      };
      window.addEventListener('storage', handleStorageChange);
      
      // Listen for page visibility changes (when user switches back to tab)
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          console.log('🛒 CustomerHeader: Page visible, refreshing cart count');
          updateCartCount();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Listen for window focus (when user clicks back into the window)
      const handleFocus = () => {
        console.log('🛒 CustomerHeader: Window focused, refreshing cart count');
        updateCartCount();
      };
      window.addEventListener('focus', handleFocus);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('cartUpdated', handleCartUpdate);
        window.removeEventListener('storage', handleStorageChange);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [propCartCount]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent) => {
      setLanguage(event.detail.language);
    };
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
  }, []);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate('/app'); // Navigate to Dashboard by default
    }
  };

  const _handleSearch = (e: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent) => {
    if (e.type === 'keydown' && (e as React.KeyboardEvent).key !== 'Enter') {
      return;
    }
    
    if (searchQuery.trim()) {
      if (onSearch) {
        onSearch(searchQuery.trim());
      } else {
        navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      }
    } else {
      navigate('/search');
    }
  };

  const _handleVoiceSearch = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice recognition not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = language || 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      if (onSearch) {
        onSearch(transcript);
      } else {
        navigate(`/search?q=${encodeURIComponent(transcript)}`);
      }
    };
    recognition.onerror = () => {};
    recognition.start();
  };

  const handleCartClick = () => {
    if (onCartClick) {
      onCartClick();
    } else {
      navigate('/app', { state: { view: 'cart' } });
    }
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: lang } }));
  };

  const headerClass = `customer-header ${variant === 'sticky' ? 'sticky-header' : variant === 'compact' ? 'compact-header' : ''} ${showBackButton ? 'show-back-button' : ''} ${className}`.trim();

  return (
    <header className={headerClass} role="banner" aria-label="Site header">
      <div className="header-content">
        {/* Back Button - Show when showBackButton prop is true */}
        {showBackButton && (
          <button 
            className="header-back-button"
            onClick={handleBackClick}
            aria-label="Back to Dashboard"
            title="Back to Dashboard"
          >
            <span style={{ fontSize: '32px', lineHeight: 1 }}>←</span>
          </button>
        )}

        {/* Logo */}
        <div className="header-logo" onClick={() => navigate('/app')}>
          <Logo size="large" variant="default" />
        </div>
        
        {/* Welcome Message - Compact */}
        {isAuthenticated && user && (
          <div className="header-welcome-message" style={{ fontSize: '14px' }}>
            <span className="header-welcome-text">{t.welcomeBack || 'Welcome back,'}</span>
            <span className="header-user-name">{user.first_name}!</span>
          </div>
        )}
        
        {/* Search Bar */}
        <div className="header-search-container">
          <div className="header-search-wrapper">
            <span className="header-search-icon">🔍</span>
            <input
              type="text"
              className="header-search-input"
              placeholder={t.searchPlaceholder || "What can we help you find?"}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  if (onSearch) {
                    onSearch(searchQuery.trim());
                  } else {
                    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                  }
                }
              }}
              onClick={() => {
                navigate('/search');
              }}
              aria-label="Search products, shops, brands"
            />
          </div>
        </div>
        
        {/* Language Selector and Action Buttons */}
        <div className="header-right">
          <div className="language-selector-wrapper">
            <LanguageSelector
              currentLanguage={language}
              onLanguageChange={handleLanguageChange}
              variant="minimal"
            />
          </div>
          <button 
            className="header-action-btn orders-btn"
            onClick={() => navigate('/orders')}
            aria-label="Orders"
            title="Orders"
          >
            <img 
              src="/icons/orders-icon.svg" 
              alt="Orders" 
              className="header-action-icon"
              onError={(e) => {
                const button = e.currentTarget.parentElement;
                if (button) {
                  button.innerHTML = '<span class="header-action-icon">📦</span>';
                }
              }}
            />
          </button>
          <button 
            className="header-action-btn cart-btn"
            onClick={handleCartClick}
            aria-label="Shopping Cart"
            title="Shopping Cart"
          >
            <img 
              src="/icons/cart-icon.svg" 
              alt="Shopping Cart" 
              className="header-action-icon"
              onError={(e) => {
                const button = e.currentTarget.parentElement;
                if (button) {
                  const badge = button.querySelector('.action-badge');
                  button.innerHTML = '<span class="header-action-icon">🛒</span>';
                  if (badge && cartCount > 0) {
                    button.appendChild(badge);
                  }
                }
              }}
            />
            {cartCount > 0 && (
              <span className="action-badge">{cartCount}</span>
            )}
          </button>
          {showLoyaltyButton && (
            <button 
              className="header-action-btn loyalty-btn"
              onClick={() => navigate('/loyalty')}
              aria-label="Loyalty Points"
              title="Loyalty Points"
            >
              <img 
                src="/icons/loyalty-icon.svg" 
                alt="Loyalty Points" 
                className="header-action-icon"
                onError={(e) => {
                  const button = e.currentTarget.parentElement;
                  if (button) {
                    button.innerHTML = '<span class="header-action-icon">⭐</span>';
                  }
                }}
              />
            </button>
          )}
          <button 
            className="notification-btn"
            onClick={() => navigate('/notifications')}
            aria-label="Notifications"
          >
            <img 
              src="/icons/notification-icon.svg" 
              alt="Notifications" 
              className="notification-icon"
              onError={(e) => {
                const button = e.currentTarget.parentElement;
                if (button) {
                  const badge = button.querySelector('.notification-badge');
                  button.innerHTML = '<span class="notification-icon">🔔</span>';
                  if (badge) {
                    button.appendChild(badge);
                  }
                }
              }}
            />
            {notificationCountState > 0 && (
              <span className="notification-badge">{notificationCountState}</span>
            )}
          </button>
        </div>
        
        {/* Profile Button */}
        <div className="header-actions">
          <button 
            className="header-action-btn profile-btn"
            onClick={() => navigate('/profile')}
            aria-label="Profile"
            title="Profile"
          >
            <span className="header-action-icon">👤</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default CustomerHeader;


